const MIN_OVERLAP_CHARS = 48;

/** Gmail-style ("Le … a écrit :") and Yoda composer ("Name a écrit :" / "Name wrote:"). */
const CITATION_HEADER_RE =
    /^(?:on\s.+wrote\s*:|le\s.+a écrit\s*:|.+\s+a écrit\s*:|.+\s+wrote\s*:|am\s.+schrieb\s*.*:)\s*$/i;

const CITATION_HEADER_SUFFIX_RE =
    /(?:\n|^)[ \t]*(?:on\s.+wrote\s*:|le\s.+a écrit\s*:|.+\s+a écrit\s*:|.+\s+wrote\s*:|am\s.+schrieb\s*.*:)[ \t]*(?:\n[ \t]*)*$/i;

export type CollapsedQuoteSplit = {
    visibleHtml: string;
    quotedHtml: string;
};

export type QuotePlainRange = {
    start: number;
    end: number;
};

/**
 * Extract visible text from an HTML fragment.
 * `<br>` becomes a newline so Gmail citation headers stay line-oriented.
 */
export function htmlToPlainText(html: string): string {
    if (!html)
        return '';

    const doc = new DOMParser().parseFromString(`<div id="yoda-root">${html}</div>`, 'text/html');
    const root = doc.getElementById('yoda-root') || doc.body;
    root.querySelectorAll('br').forEach((br) => {
        br.replaceWith(doc.createTextNode('\n'));
    });
    return root.textContent || '';
}

/**
 * Normalize plain text for quote overlap matching:
 * strip quote markers, citation headers, collapse whitespace, lowercase.
 */
export function normalizeForQuoteMatch(text: string): string {
    if (!text)
        return '';

    const lines = text
        .replace(/\u00a0/g, ' ')
        .replace(/\r\n/g, '\n')
        .split('\n');

    const kept: string[] = [];
    for (const line of lines) {
        const stripped = line.replace(/^\s*>+\s?/, '').trim();
        if (!stripped)
            continue;
        if (CITATION_HEADER_RE.test(stripped))
            continue;
        kept.push(stripped.replace(/[ \t]+/g, ' '));
    }

    return kept.join('\n').trim().toLowerCase();
}

type NormMap = {
    norm: string;
    /** norm index → index in the original plain string */
    toPlain: number[];
};

/**
 * Build a normalized string while keeping a map back to plain-text offsets.
 */
export function buildNormalizedWithMap(plain: string): NormMap {
    const toPlain: number[] = [];
    let norm = '';

    const source = plain.replace(/\u00a0/g, ' ').replace(/\r\n/g, '\n');
    const lines = source.split('\n');
    let plainIdx = 0;

    for (let li = 0; li < lines.length; li++) {
        const line = lines[li];
        const quoteMatch = line.match(/^\s*>+\s?/);
        const contentStart = quoteMatch ? quoteMatch[0].length : 0;
        const content = line.slice(contentStart);
        const trimmed = content.trim();

        if (trimmed && !CITATION_HEADER_RE.test(trimmed)) {
            if (norm.length > 0) {
                norm += '\n';
                toPlain.push(plainIdx + contentStart);
            }

            let seenContent = false;
            for (let i = contentStart; i < line.length; i++) {
                const ch = line[i];
                if (ch === ' ' || ch === '\t') {
                    if (!seenContent || norm.endsWith(' ') || norm.endsWith('\n'))
                        continue;
                    norm += ' ';
                    toPlain.push(plainIdx + i);
                    continue;
                }
                seenContent = true;
                norm += ch.toLowerCase();
                toPlain.push(plainIdx + i);
            }
        }

        plainIdx += line.length + (li < lines.length - 1 ? 1 : 0);
    }

    // Trim leading/trailing whitespace in norm while keeping map aligned
    let start = 0;
    let end = norm.length;
    while (start < end && (norm[start] === ' ' || norm[start] === '\n'))
        start += 1;
    while (end > start && (norm[end - 1] === ' ' || norm[end - 1] === '\n'))
        end -= 1;

    return {
        norm: norm.slice(start, end),
        toPlain: toPlain.slice(start, end),
    };
}

/**
 * Expand a plain-text quote start backward to include a citation header line.
 */
export function expandToCitationHeader(plain: string, start: number): number {
    if (start <= 0)
        return start;

    const before = plain.slice(0, start);
    const match = before.match(CITATION_HEADER_SUFFIX_RE);
    if (!match)
        return start;

    const headerAt = before.length - match[0].length;
    if (plain[headerAt] === '\n')
        return headerAt + 1;
    return headerAt;
}

type CandidateRange = {
    normStart: number;
    normEnd: number;
};

/**
 * Find the plain-text range of quoted history in `currentPlain`.
 * Returns null when no reliable overlap is found.
 *
 * Important: for quotes in the middle (composer leading quote + fresh reply),
 * `end` stops after the overlapped previous body so the reply stays visible.
 */
export function findQuotePlainRange(currentPlain: string, previousPlains: string[]): QuotePlainRange | null {
    const current = buildNormalizedWithMap(currentPlain);
    if (current.norm.length < MIN_OVERLAP_CHARS)
        return null;

    let best: CandidateRange | null = null;

    const consider = (normStart: number, normEnd: number) => {
        if (normStart < 0 || normEnd <= normStart || normEnd > current.norm.length)
            return;
        // Avoid collapsing the whole message (no fresh content left).
        if (normStart === 0 && normEnd >= current.norm.length)
            return;
        if (!best || normStart < best.normStart)
            best = { normStart, normEnd };
    };

    for (const previousPlain of previousPlains) {
        const prev = normalizeForQuoteMatch(previousPlain);
        if (prev.length < MIN_OVERLAP_CHARS)
            continue;

        // Full previous body appears inside current (typical email / composer quote).
        const fullAt = current.norm.indexOf(prev);
        if (fullAt >= 0) {
            consider(fullAt, fullAt + prev.length);
            continue;
        }

        // Longest suffix of current that is contained in a previous message (trailing quote).
        let lo = MIN_OVERLAP_CHARS;
        let hi = Math.min(current.norm.length, prev.length);
        let foundLen = -1;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            const suffix = current.norm.slice(current.norm.length - mid);
            if (prev.includes(suffix)) {
                foundLen = mid;
                lo = mid + 1;
            } else {
                hi = mid - 1;
            }
        }
        if (foundLen >= MIN_OVERLAP_CHARS) {
            const start = current.norm.length - foundLen;
            if (start > 0)
                consider(start, current.norm.length);
        }

        // Substantial prefix of a previous message appears in current (truncated quote).
        const maxPrefix = Math.min(prev.length, current.norm.length);
        for (let len = maxPrefix; len >= MIN_OVERLAP_CHARS; len -= 1) {
            const prefix = prev.slice(0, len);
            const at = current.norm.indexOf(prefix);
            if (at >= 0) {
                consider(at, at + len);
                break;
            }
        }
    }

    if (!best)
        return null;

    const { normStart, normEnd } = best;
    if (normStart >= current.toPlain.length)
        return null;

    const plainStart = current.toPlain[normStart];
    if (plainStart == null || plainStart < 0)
        return null;

    const lastMapped = current.toPlain[Math.min(normEnd, current.toPlain.length) - 1];
    if (lastMapped == null)
        return null;

    let plainEnd = lastMapped + 1;
    // Trailing quote: keep through EOF so we don't leave orphan whitespace in visible.
    if (normEnd >= current.norm.length)
        plainEnd = currentPlain.length;

    const start = expandToCitationHeader(currentPlain, plainStart);
    if (start >= plainEnd)
        return null;

    return { start, end: plainEnd };
}

/**
 * Find the plain-text index where quoted history starts in `currentPlain`.
 * Returns -1 when no reliable overlap is found.
 */
export function findQuoteStartPlainIndex(currentPlain: string, previousPlains: string[]): number {
    const range = findQuotePlainRange(currentPlain, previousPlains);
    return range ? range.start : -1;
}

type PlainBoundary = {
    node: Node;
    offset: number;
};

/**
 * Walk the DOM counting plain text the same way as `htmlToPlainText` (`<br>` → 1 char).
 */
function locatePlainBoundary(root: Element, plainOffset: number): PlainBoundary | null {
    if (plainOffset < 0)
        return null;

    let counted = 0;
    const visit = (node: Node): PlainBoundary | null => {
        if (node.nodeName === 'BR') {
            if (counted === plainOffset)
                return { node, offset: 0 };
            counted += 1;
            if (counted === plainOffset) {
                // Boundary falls just after this <br>.
                const next = node.nextSibling;
                if (next)
                    return { node: next, offset: 0 };
                return { node, offset: 1 };
            }
            return null;
        }

        if (node.nodeType === Node.TEXT_NODE) {
            const len = node.textContent?.length || 0;
            if (counted + len > plainOffset)
                return { node, offset: plainOffset - counted };
            counted += len;
            if (counted === plainOffset) {
                const next = node.nextSibling;
                if (next)
                    return { node: next, offset: 0 };
                return { node, offset: len };
            }
            return null;
        }

        for (let child = node.firstChild; child; child = child.nextSibling) {
            const found = visit(child);
            if (found)
                return found;
        }
        return null;
    };

    return visit(root);
}

function setRangeBoundary(
    range: Range,
    boundary: PlainBoundary,
    which: 'start' | 'end'
): boolean {
    const { node, offset } = boundary;
    try {
        if (node.nodeName === 'BR') {
            if (offset <= 0) {
                if (which === 'start')
                    range.setStartBefore(node);
                else
                    range.setEndBefore(node);
            } else {
                if (which === 'start')
                    range.setStartAfter(node);
                else
                    range.setEndAfter(node);
            }
            return true;
        }
        if (node.nodeType === Node.TEXT_NODE) {
            const len = node.textContent?.length || 0;
            const clamped = Math.max(0, Math.min(offset, len));
            if (which === 'start')
                range.setStart(node, clamped);
            else
                range.setEnd(node, clamped);
            return true;
        }
        // Element boundary with offset 0 → before node.
        if (which === 'start')
            range.setStartBefore(node);
        else
            range.setEndBefore(node);
        return true;
    } catch (_e) {
        return false;
    }
}

/**
 * Split an HTML fragment so that text at/after `plainOffset` becomes `quotedHtml`.
 */
export function splitHtmlAtPlainOffset(html: string, plainOffset: number): CollapsedQuoteSplit {
    if (!html || plainOffset <= 0)
        return { visibleHtml: html || '', quotedHtml: '' };

    const plainLength = htmlToPlainText(html).length;
    return splitHtmlByPlainRange(html, plainOffset, plainLength);
}

/**
 * Extract `[plainStart, plainEnd)` as `quotedHtml`; keep the rest as `visibleHtml`.
 * Fresh content after a leading quote stays visible.
 */
export function splitHtmlByPlainRange(
    html: string,
    plainStart: number,
    plainEnd: number
): CollapsedQuoteSplit {
    if (!html || plainStart < 0 || plainEnd <= plainStart)
        return { visibleHtml: html || '', quotedHtml: '' };

    const doc = new DOMParser().parseFromString(`<div id="yoda-root">${html}</div>`, 'text/html');
    const root = doc.getElementById('yoda-root');
    if (!root)
        return { visibleHtml: html, quotedHtml: '' };

    const plainLength = htmlToPlainText(html).length;
    const end = Math.min(plainEnd, plainLength);
    if (plainStart >= plainLength || end <= plainStart)
        return { visibleHtml: html, quotedHtml: '' };

    const startBoundary = locatePlainBoundary(root, plainStart);
    if (!startBoundary)
        return { visibleHtml: html, quotedHtml: '' };

    const range = doc.createRange();
    if (!setRangeBoundary(range, startBoundary, 'start'))
        return { visibleHtml: html, quotedHtml: '' };

    if (end >= plainLength) {
        if (!root.lastChild)
            return { visibleHtml: html, quotedHtml: '' };
        range.setEndAfter(root.lastChild);
    } else {
        const endBoundary = locatePlainBoundary(root, end);
        if (!endBoundary || !setRangeBoundary(range, endBoundary, 'end'))
            return { visibleHtml: html, quotedHtml: '' };
    }

    let quotedFragment: DocumentFragment;
    try {
        quotedFragment = range.extractContents();
    } catch (_e) {
        return { visibleHtml: html, quotedHtml: '' };
    }

    const quotedWrapper = doc.createElement('div');
    quotedWrapper.appendChild(quotedFragment);

    return {
        visibleHtml: root.innerHTML,
        quotedHtml: quotedWrapper.innerHTML,
    };
}

function isEffectivelyEmptyHtml(html: string): boolean {
    return normalizeForQuoteMatch(htmlToPlainText(html)).length === 0;
}

function isSkippableWhitespaceNode(node: Node): boolean {
    if (node.nodeName === 'BR')
        return true;
    if (node.nodeType === Node.TEXT_NODE)
        return !(node.textContent || '').trim();
    return false;
}

function isEmptyBreakOnlyElement(node: Node): boolean {
    if (node.nodeType !== Node.ELEMENT_NODE)
        return false;
    const el = node as Element;
    if (el.nodeName !== 'P' && el.nodeName !== 'DIV')
        return false;
    const kids = Array.from(el.childNodes).filter((n) => !isSkippableWhitespaceNode(n));
    if (!kids.length)
        return true;
    return kids.every((n) => n.nodeName === 'BR');
}

/**
 * True when the element is only a citation header (e.g. "Le … a écrit :"),
 * ignoring nested blockquotes for the text check.
 */
function isCitationHeaderElement(node: Node): boolean {
    if (node.nodeType !== Node.ELEMENT_NODE)
        return false;
    const el = node as Element;
    if (el.nodeName !== 'P' && el.nodeName !== 'DIV')
        return false;

    const clone = el.cloneNode(true) as Element;
    clone.querySelectorAll('blockquote').forEach((bq) => bq.remove());
    const text = (clone.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
    if (CITATION_HEADER_RE.test(text))
        return true;

    const onlyEm = el.children.length === 1 && el.children[0].nodeName === 'EM';
    if (onlyEm) {
        const emText = (el.children[0].textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
        return CITATION_HEADER_RE.test(emText) || /wrote\s*:?\s*$/i.test(emText) || /a écrit\s*:?\s*$/i.test(emText);
    }
    return false;
}

/**
 * Gmail wraps "Le … a écrit :" + blockquote in a trailing DIV (not a root BLOCKQUOTE).
 */
function isGmailTrailingQuoteWrapper(node: Node): boolean {
    if (node.nodeType !== Node.ELEMENT_NODE)
        return false;
    const el = node as Element;
    if (el.nodeName !== 'DIV')
        return false;

    const kids = Array.from(el.childNodes).filter((n) => !isSkippableWhitespaceNode(n));
    if (!kids.length)
        return false;

    let hasBlockquote = false;
    for (const kid of kids) {
        if (kid.nodeName === 'BLOCKQUOTE') {
            hasBlockquote = true;
            continue;
        }
        if (isCitationHeaderElement(kid))
            continue;
        return false;
    }
    return hasBlockquote;
}

function isLeadingCitationNode(node: Node): boolean {
    if (isCitationHeaderElement(node))
        return true;
    if (node.nodeType !== Node.ELEMENT_NODE)
        return false;
    const el = node as Element;
    if (el.nodeName !== 'P' && el.nodeName !== 'DIV')
        return false;
    const text = (el.textContent || '').trim();
    const onlyEm = el.children.length === 1 && el.children[0].nodeName === 'EM';
    return onlyEm || /wrote\s*:?\s*$/i.test(text) || /a écrit\s*:?\s*$/i.test(text);
}

/**
 * Fallback: collapse trailing blockquote(s), optionally preceded by a short citation line.
 * Also peels Gmail's trailing wrapper DIV around citation + blockquote.
 */
export function splitTrailingBlockquoteQuote(html: string): CollapsedQuoteSplit | null {
    if (!html)
        return null;

    const doc = new DOMParser().parseFromString(`<div id="yoda-root">${html}</div>`, 'text/html');
    const root = doc.getElementById('yoda-root');
    if (!root || !root.childNodes.length)
        return null;

    const children = Array.from(root.childNodes);
    let start = children.length;

    for (let i = children.length - 1; i >= 0; i--) {
        const child = children[i];
        if (isSkippableWhitespaceNode(child)) {
            start = i;
            continue;
        }
        if (child.nodeName === 'BLOCKQUOTE' || isGmailTrailingQuoteWrapper(child)) {
            start = i;
            continue;
        }
        if (isCitationHeaderElement(child) && start < children.length) {
            start = i;
            continue;
        }
        break;
    }

    if (start <= 0 || start >= children.length)
        return null;

    const quotedWrapper = doc.createElement('div');
    for (let i = start; i < children.length; i++)
        quotedWrapper.appendChild(children[i].cloneNode(true));

    for (let i = children.length - 1; i >= start; i--)
        root.removeChild(children[i]);

    const visibleHtml = root.innerHTML;
    const quotedHtml = quotedWrapper.innerHTML;
    if (isEffectivelyEmptyHtml(visibleHtml) || isEffectivelyEmptyHtml(quotedHtml))
        return null;

    return { visibleHtml, quotedHtml };
}

/**
 * Fallback: collapse a leading Yoda-style citation + blockquote (composer quote).
 */
export function splitLeadingBlockquoteQuote(html: string): CollapsedQuoteSplit | null {
    if (!html)
        return null;

    const doc = new DOMParser().parseFromString(`<div id="yoda-root">${html}</div>`, 'text/html');
    const root = doc.getElementById('yoda-root');
    if (!root || !root.childNodes.length)
        return null;

    const children = Array.from(root.childNodes);
    let end = -1;

    for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child.nodeType === Node.TEXT_NODE && !(child.textContent || '').trim()) {
            if (end >= 0)
                end = i;
            continue;
        }
        if (isEmptyBreakOnlyElement(child)) {
            // Composer inserts `<p><br></p>` between quote and reply — keep with quote.
            if (end >= 0)
                end = i;
            continue;
        }
        if (child.nodeName === 'BLOCKQUOTE') {
            end = i;
            continue;
        }
        if (isLeadingCitationNode(child)) {
            end = i;
            continue;
        }
        break;
    }

    if (end < 0 || end >= children.length - 1)
        return null;

    // Require at least one blockquote in the leading run.
    const leading = children.slice(0, end + 1);
    if (!leading.some((n) => n.nodeName === 'BLOCKQUOTE'))
        return null;

    // Drop trailing empty break-only nodes from the quote run so visible starts at real content.
    while (end >= 0 && isEmptyBreakOnlyElement(children[end]))
        end -= 1;
    if (end < 0 || !children.slice(0, end + 1).some((n) => n.nodeName === 'BLOCKQUOTE'))
        return null;
    if (end >= children.length - 1)
        return null;

    const quotedWrapper = doc.createElement('div');
    for (let i = 0; i <= end; i++)
        quotedWrapper.appendChild(children[i].cloneNode(true));

    for (let i = end; i >= 0; i--)
        root.removeChild(children[i]);

    // Also strip a single leading empty break left for the caret after the quote.
    while (root.firstChild && isEmptyBreakOnlyElement(root.firstChild))
        root.removeChild(root.firstChild);
    while (root.firstChild && isSkippableWhitespaceNode(root.firstChild))
        root.removeChild(root.firstChild);

    const visibleHtml = root.innerHTML;
    const quotedHtml = quotedWrapper.innerHTML;
    if (isEffectivelyEmptyHtml(visibleHtml) || isEffectivelyEmptyHtml(quotedHtml))
        return null;

    return { visibleHtml, quotedHtml };
}

function isValidSplit(split: CollapsedQuoteSplit | null): split is CollapsedQuoteSplit {
    return !!(
        split
        && split.quotedHtml
        && !isEffectivelyEmptyHtml(split.visibleHtml)
        && !isEffectivelyEmptyHtml(split.quotedHtml)
    );
}

/**
 * Split HTML into fresh content + collapsible quoted history.
 * Prefers Yoda leading composer quotes, then real overlap with previous messages,
 * then trailing blockquote heuristics.
 */
export function splitQuotedHtml(html: string, previousHtmls: string[] = []): CollapsedQuoteSplit | null {
    if (!html)
        return null;

    // Composer replies put the quote first; prefer that over overlap-to-EOF mistakes.
    const leading = splitLeadingBlockquoteQuote(html);
    if (isValidSplit(leading))
        return leading;

    const previousPlains = (previousHtmls || [])
        .map((item) => htmlToPlainText(item || ''))
        .filter((text) => normalizeForQuoteMatch(text).length >= MIN_OVERLAP_CHARS);

    if (previousPlains.length > 0) {
        const currentPlain = htmlToPlainText(html);
        const range = findQuotePlainRange(currentPlain, previousPlains);
        if (range) {
            const split = splitHtmlByPlainRange(html, range.start, range.end);
            if (isValidSplit(split))
                return split;
        }
    }

    return splitTrailingBlockquoteQuote(html);
}
